/** CONTENU AUTOMATIQUE — la semaine Instagram déjà écrite.
 *  Le pro lit, retouche s'il veut, copie, poste. */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, RefreshCw } from "lucide-react";
import { api, messageErreur } from "../api";
import { Btn, EnTete, Erreur, Micro, Squelette } from "../components/Bits";
import { urlPhoto } from "../components/Photos";

const FORMAT_LABEL = {
  post: "Publication",
  carrousel: "Carrousel",
  story: "Story",
  reel: "Reel",
};

export default function Contenu() {
  const [plan, setPlan] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const { data } = await api.get("/contenu/semaine", { timeout: 120000 });
      setPlan(data);
    } catch (e) {
      setErreur(messageErreur(e, "Impossible de charger ton plan de la semaine."));
    } finally {
      setCharge(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const regenerer = async () => {
    setOccupe(true);
    try {
      const { data } = await api.post("/contenu/semaine", null, { timeout: 120000 });
      setPlan(data);
      toast.success("Nouvelle semaine écrite.");
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  };

  const copier = async (p) => {
    const texte = [p.legende, (p.hashtags || []).map((h) => `#${h}`).join(" ")]
      .filter(Boolean)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(texte);
      toast.success("Légende copiée. Colle-la dans Instagram.");
    } catch {
      toast.error("Copie impossible sur ce navigateur.");
    }
  };

  const basculer = async (p) => {
    setPlan((v) => ({
      ...v,
      posts: v.posts.map((x) => (x.date === p.date ? { ...x, publie: !x.publie } : x)),
    }));
    try {
      await api.patch("/contenu/semaine", { date: p.date, publie: !p.publie });
    } catch (e) {
      toast.error(messageErreur(e));
      charger();
    }
  };

  if (charge) return <Squelette lignes={4} />;
  if (erreur) return <Erreur message={erreur} surReessai={charger} />;

  return (
    <div className="pb-8" data-testid="page-contenu">
      <EnTete
        micro="Contenu de la semaine"
        titre="Ta semaine Instagram, déjà écrite"
        sous="Écrit à partir de tes prestations, de tes vraies places libres et de tes photos."
        actions={
          <Btn variante="fantome" icone={RefreshCw} onClick={regenerer} disabled={occupe}
            data-testid="contenu-regenerer">
            Régénérer
          </Btn>
        }
      />

      <p className="text-[13px] mt-3 num" style={{ color: "var(--text-subtle)" }}>
        Semaine {plan?.semaine_label} · {plan?.posts?.length || 0} publications
      </p>

      <div className="mt-6 space-y-3 max-w-[720px]">
        {(plan?.posts || []).map((p, i) => (
          <article
            key={`${p.date}-${i}`}
            data-testid={`contenu-post-${i}`}
            className="p-4"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              opacity: p.publie ? 0.62 : 1,
              transition: "opacity .2s ease",
            }}
          >
            <div className="flex items-start gap-4">
              {p.photo ? (
                <img
                  src={urlPhoto(p.photo.url)}
                  alt=""
                  className="h-20 w-20 shrink-0 object-cover"
                  style={{ borderRadius: "var(--radius-md)" }}
                />
              ) : (
                <div
                  className="h-20 w-20 shrink-0"
                  style={{ borderRadius: "var(--radius-md)", background: "var(--accent-soft)" }}
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <Micro>{p.jour_label}</Micro>
                  <span className="num text-[12px]" style={{ color: "var(--text-subtle)" }}>
                    {p.heure}
                  </span>
                  <span
                    className="text-[11.5px] font-semibold px-2 py-0.5"
                    style={{
                      borderRadius: "var(--radius-full)",
                      background: "var(--accent-soft)",
                      color: "var(--accent)",
                    }}
                  >
                    {FORMAT_LABEL[p.format] || p.format}
                  </span>
                </div>
                <p className="text-[16px] font-semibold mt-1.5">{p.accroche}</p>
                <p className="text-[12.5px]" style={{ color: "var(--text-subtle)" }}>
                  {p.angle}
                </p>
              </div>
            </div>

            <p
              className="text-[14.5px] mt-3 whitespace-pre-line"
              style={{ color: "var(--text)" }}
              data-testid={`contenu-legende-${i}`}
            >
              {p.legende}
            </p>

            {(p.hashtags || []).length > 0 && (
              <p className="text-[13px] mt-2" style={{ color: "var(--accent)" }}>
                {p.hashtags.map((h) => `#${h}`).join(" ")}
              </p>
            )}

            <div className="flex gap-2 mt-3.5">
              <Btn variante="fantome" icone={Copy} onClick={() => copier(p)} data-testid={`contenu-copier-${i}`}>
                Copier
              </Btn>
              <Btn
                variante="nu"
                icone={Check}
                onClick={() => basculer(p)}
                data-testid={`contenu-publie-${i}`}
              >
                {p.publie ? "Posté" : "Marquer posté"}
              </Btn>
            </div>
          </article>
        ))}
      </div>

      <p className="text-[12.5px] mt-6 max-w-[560px]" style={{ color: "var(--text-subtle)" }}>
        Servae n'a pas encore le droit de publier à ta place sur Instagram : tu copies, tu colles,
        tu gardes la main sur ce qui sort de ton compte.
      </p>
    </div>
  );
}
